/**
 * createTableInteractions.ts - The table's user operations, exposed on the model as
 * `TableModel.interactions`.
 *
 * The table is layered as:
 * - the component (`TableEditable`): decodes events, owns the React lifecycle and modals;
 * - `model.interactions` (this module): table-wide user operations such as selecting, editing,
 *   pasting, dragging, filling down and freezing. They may touch the DOM, focus, scroll, the cell
 *   editor position and warnings, and return nothing or a status, never DOM or raw data;
 * - `model.mutations`: data and state writes, with no DOM.
 *
 * The interactions are built once per table by `TableModelProvider`. Every method reads the latest
 * model, props and mutations from the store when called, so the object and its methods keep their
 * identity for the table's lifetime even when the mutations are rebuilt.
 *
 * DOM access, element refs, timers and the {@link TableViewport} are implementation details: the
 * elements live in a private registry that the component fills through the write-only
 * {@link TableElementAttachments}, and the table counts as mounted while its focus bearer is attached.
 * Do not add getters that return DOM or live model data. {@link TableInteractions.classBridge} is the
 * one exception, a temporary workaround for `TableEditableClass` while it is being detangled.
 */

import $ from 'jquery';
import type React from 'react';
import isEqual from 'react-fast-compare';
import type {TableVirtuosoHandle} from 'react-virtuoso';

import {assert} from 'Asserts';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {TableWYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {
  MultiSelectionBox,
  TABLE_SELECTION_ROW,
  TableDimension,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import {
  showFreezeMergeConflictToast,
  wouldFreezeExceedViewport,
} from 'notebook/tables/core-ui/FreezeColumnUtils';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {TryInsertOptions} from 'notebook/tables/excess/FormulaUtil';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  createTableViewport,
  TableElements,
  TablePointerPosition,
  TableViewport,
} from 'notebook/tables/store/logic/interactions/createTableViewport';
import {parseRawDataToCells} from 'notebook/tables/store/logic/interactions/tableInteractionLogic';
import {computeFillDownDragBox} from 'notebook/tables/store/logic/TableFillDownLogic';
import {computeCellCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeContainsMergedRegion} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {
  computeExpandedSelectionIndex,
  computeMultiIndicesState,
  getMultiEditingText,
  getMultiEditingTypedValue,
} from 'notebook/tables/store/logic/TableMultiSelectionLogic';
import {
  computeExpandedSelectionBox,
  computeIsRealIndex,
} from 'notebook/tables/store/logic/TableSelectionLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import {ClipboardItemType} from 'util/clipboard/ClipboardUtil';
import {ElementClipboardEvent, ElementMouseEvent, Optional} from 'util/Types';

export interface CellDragStartArgs {
  start: TableIndex;
  end?: TableIndex;
  active?: boolean | string;
  allowSingleOverride?: boolean;
}

export interface AxisCellDragStartArgs {
  start: TableIndex;
  active: string;
}

export interface TablePasteOptions {
  transpose?: boolean;
  type: ClipboardItemType;
  selectionIndex?: TableIndex;
  /** When true, pasted values replace cell content but destination formatting is kept. */
  preserveDestinationFormatting?: boolean;
}

/**
 * The handlers that cells, axis cells and the header row read from their model's `interactions`.
 * Every method keeps its identity for the table's lifetime and does nothing while the table is not
 * mounted.
 */
export interface TableInteractionHandle {
  /** Starts (or shift-extends) a whole row or column selection from a mouse down on an axis cell. */
  axisCellDragStart: (options: AxisCellDragStartArgs, e: ElementMouseEvent) => void;
  /** Extends the in-progress row or column selection to the axis cell under the pointer. */
  axisCellDragOver: (index: TableIndex, e: ElementMouseEvent) => void;
  /**
   * Starts dragging the selected rows or columns to reorder them, selecting just the dragged one
   * when it is not already selected. Returns whether the drag started.
   */
  axisCellDragHandleMouseDown: (draggingDimension: TableDimension, index: number) => boolean;
  /**
   * Finalizes any uncommitted text so that no cell is in edit mode, keeping the selection. Safe to
   * call when there is no active edit.
   */
  finalizeAndExitEditMode: () => void;
  /** Freezes the columns up to and including column `j`, unless the next column would not fit. */
  freezeThroughColumn: (j: number) => void;
  /** Unfreezes the columns from column `j` onward. */
  unfreezeFromColumn: (j: number) => void;
  /** Starts (or shift-extends) a selection from a mouse down on a cell. */
  cellDragStart: (args: CellDragStartArgs, e: ElementMouseEvent) => void;
  /** Extends the in-progress drag selection to the cell under the pointer. */
  cellDragOver: (index: TableIndex) => void;
  /** Resizes the in-progress fill-down box to reach the cell under the pointer. */
  cellMouseMoveHandler: (index: TableIndex) => void;
  /** Starts a fill-down drag from the selection's fill-down square. */
  fillDownSquareMouseDownHandler: (e: ElementMouseEvent) => void;
  /** Fills the selection down through the following blank rows. */
  fillDownSquareDoubleClickHandler: (e: ElementMouseEvent) => void;
  /**
   * Finalizes any in-progress edit, then selects the cell at `index`, starting an edit there (and
   * positioning the cell editor over it) when `options.editing` is set.
   */
  selectCell: (index: TableIndex, options: MultiSelectListUtil.MultiSelectSingleParam<TableIndex>) => void;
  /**
   * Starts editing the cell at `index`, first growing the table to contain it when it is a ghost
   * cell. Places the caret at the end of the cell's text, or selects all of it when `selectAll` is
   * set. Warns and does nothing when the selection is locked.
   */
  expandToAndEditCell: (index: TableIndex, selectAll?: boolean) => void;
  /**
   * Pastes the clipboard into the table at the selection: a pasted image is attached to the selected
   * cell when it accepts files, and anything else is parsed as a table (HTML when the clipboard holds
   * a single table, otherwise tab-separated text). Must not be called while the table is locked.
   */
  multiPasteHandler: (e: ElementClipboardEvent, options?: TablePasteOptions) => void;
}

/**
 * Write-only, stable callback refs that register the table's elements with the interactions. Render
 * them as `ref={interactions.attach.x}` (or call them from a ref the component also keeps).
 */
export interface TableElementAttachments {
  /** The table counts as mounted while its focus bearer is attached. */
  focusBearer: React.RefCallback<HTMLDivElement>;
  horizontalScrollContainer: React.RefCallback<HTMLDivElement>;
  stickyHeader: React.RefCallback<HTMLDivElement>;
  virtualizationHandle: React.RefCallback<TableVirtuosoHandle>;
  tableRoot: React.RefCallback<HTMLDivElement>;
  /** Called by `useTableMeasurement` whenever it finds (or drops) the page's vertical scroll container. */
  pageVerticalScrollContainer: (container: Element | null) => void;
}

/**
 * What `TableEditableClass` still reaches into while its keyboard, formula box, scroll and render code
 * is detangled. A temporary workaround: nothing new should depend on it.
 */
export interface TableInteractionClassBridge {
  viewport: TableViewport;
  /**
   * Selects the cell at `index`. When `param.editing` is set, enters edit mode and positions the cell
   * editor over the cell.
   */
  multiSelectSingle: (
    index: TableIndex,
    param: MultiSelectListUtil.MultiSelectSingleParam<TableIndex> | undefined
  ) => void;
  /** Sets the selection's anchor and end, syncs the anchor's editing state and focuses the table. */
  setMultiIndices: (
    indices: {start: TableIndex; end: TableIndex; text?: string},
    options?: {multiSelIndex?: TableIndex | null}
  ) => void;
  /** Grows the table to contain `index` and returns the (empty) cell now there. */
  expandToCell: (index: TableIndex) => Cell;
}

/** The table's user operations. See the module docstring for the rules they follow. */
export interface TableInteractions extends TableInteractionHandle {
  /**
   * Commits the in-progress cell edit, if there is one. Unlike the other interactions this also runs
   * while the table is unmounting, so that an edit is not lost when the table goes away.
   */
  finalizeEdit: () => void;
  /** Records the pointer position over the table's scroll area, for drags that continue while scrolling. */
  trackPointer: (clientX: number, clientY: number) => void;
  attach: TableElementAttachments;
  /** @deprecated A temporary workaround for `TableEditableClass`; see {@link TableInteractionClassBridge}. */
  classBridge: TableInteractionClassBridge;
}

export interface TableInteractionParams {
  /** Reads the latest {@link TableModel} from the store. Only call from handlers. */
  getTableModel: () => TableModel;
  /** Reads the latest {@link TableProps} from the store. Only call from handlers. */
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
}

export interface MultiEditingUpdate {
  index: TableIndex | null;
  editing: boolean;
  text: string | null;
  typedValue?: Optional<TypedValue>;
  selection?: BenchlingSelection | null;
}

export function setTableMultiEditing(getTableProps: () => TableProps, e: MultiEditingUpdate): void {
  const {setMultiEditingForTable, table, documentName} = getTableProps();
  setMultiEditingForTable({tableName: table.name, documentName: documentName ?? null}, e);
}

export function tryInsertIntoTable(getTableProps: () => TableProps, options: TryInsertOptions): boolean {
  const {table, tryInsert, documentName} = getTableProps();
  const [, columnIndex] = options.index;
  // A reference to a Single Input locks its row with a $.
  if (table.columnConstraints[columnIndex]?.structuredTableColumn?.isSingleInput) {
    options.cellLock = {row: true};
  }
  return tryInsert({tableName: table.name, documentName: documentName ?? null}, options);
}

export function compareTableIndex(a: TableIndex, b: TableIndex): number {
  return a[0] - b[0] || a[1] - b[1];
}

export function createTableInteractions({
  getTableModel,
  getTableProps,
  setState,
}: TableInteractionParams): TableInteractions {
  const elements: TableElements = {
    tableRoot: null,
    focusBearer: null,
    horizontalScrollContainer: null,
    pageVerticalScrollContainer: null,
    stickyHeader: null,
    virtualizationHandle: null,
  };
  const pointerPosition: TablePointerPosition = {};
  const isMounted = (): boolean => elements.focusBearer != null;
  const viewport = createTableViewport({
    elements,
    pointerPosition,
    getTableModel,
    getTableProps,
    setState,
    isMounted,
    onPointerOverCellAfterScroll: (index) => {
      handle.cellDragOver(index);
      handle.cellMouseMoveHandler(index);
    },
  });

  const focusBearer = (): HTMLDivElement => elements.focusBearer!;

  const finalizeEdit = (): void => {
    const {multiEditing, tableNamespace, table, fileId} = getTableProps();
    const {state, mutations} = getTableModel();
    if (!(multiEditing && state.multiSelIndex != null)) {
      return;
    }
    const {formula, typedValue} = SpreadsheetStore.getState(tableNamespace, table.name, fileId);
    assert(typedValue !== undefined, 'typedValue can be null but not undefined');
    mutations.finalizeMultiEdit(state.multiSelIndex, formula, typedValue, {
      beforePersist: () => {
        setCellEditorPosition(null);
      },
    });
  };
  const setMultiEditing = (e: MultiEditingUpdate): void => {
    setTableMultiEditing(getTableProps, e);
  };
  const multiTryInsert = (options: TryInsertOptions): boolean => tryInsertIntoTable(getTableProps, options);
  const getText = (index: TableIndex): string => getMultiEditingText(getTableProps().table, index);
  const getTypedValue = (index: TableIndex): TypedValue | null =>
    getMultiEditingTypedValue(getTableProps().table, index);
  const isCrossEditing = (): boolean =>
    SpreadsheetStore.getState(getTableProps().tableNamespace).editingTable != null;

  const setMultiExtra = (
    multiExtraUpdate: {
      fillDownActive?: boolean;
      fillDownInvalid?: boolean;
      fillDownBox?: MultiSelectionBox | null;
    },
    multiExtra: TableEditableState['multiExtra']
  ): void => {
    MultiSelectListUtil.setMultiExtra(multiExtraUpdate, setState, multiExtra);
  };

  const selectionBox = (): MultiSelectionBox => {
    const {expandedBox} = getTableModel().selection;
    assert(expandedBox != null, 'Expected to have a selection');
    return expandedBox;
  };

  const cellRect = (i: number, j: number): DOMRect | undefined => {
    const scrollArea = elements.horizontalScrollContainer;
    if (!scrollArea) {
      return;
    }
    return $(scrollArea)
      .find(`[data-cid='${computeCellCid(getTableProps().cid, i, j)}']`)[0]
      ?.getBoundingClientRect();
  };

  const selectionMouseDown = (args: CellDragStartArgs, e: ElementMouseEvent): void => {
    if (!isMounted()) {
      return;
    }
    const {state, selection} = getTableModel();
    MultiSelectListUtil.multiEditableMouseDownHandler(args, e, {
      multiStartIndex: state.multiStartIndex,
      multiEndIndex: state.multiEndIndex,
      multiSelIndex: state.multiSelIndex,
      setState,
      allowSingleMultiSelect: true,
      multiTryInsert,
      multiCrossEditing: isCrossEditing(),
      multiFinalizeEditHandler: finalizeEdit,
      isIndexBetween: TableUtil.isIndexBetween,
      compareIndex: compareTableIndex,
      setMultiEditing,
      getMultiEditingText: getText,
      getMultiEditingTypedValue: getTypedValue,
      focusBearerNode: focusBearer(),
      disallowShiftExpand: !selection.hasRealSelection,
    });
  };

  const selectionMouseOver = (index: TableIndex): void => {
    if (!isMounted()) {
      return;
    }
    const {state} = getTableModel();
    MultiSelectListUtil.multiEditableMouseOverHandler(index, {
      setState,
      multiActive: state.multiActive,
      multiStartIndex: state.multiStartIndex,
      multiEndIndex: state.multiEndIndex,
      multiExtra: state.multiExtra,
      disallowMultiExpand: false,
      multiCrossEditing: isCrossEditing(),
      allowSingleMultiSelect: true,
      multiTryInsert,
      compareIndex: compareTableIndex,
      focusBearerNode: focusBearer(),
    });
  };

  const multiSelectSingle: TableInteractionClassBridge['multiSelectSingle'] = (index, param) => {
    if (!isMounted()) {
      return;
    }
    const {state} = getTableModel();
    MultiSelectListUtil.multiSelectSingle(index, param, {
      setState,
      multiStartIndex: state.multiStartIndex,
      multiEndIndex: state.multiEndIndex,
      setMultiEditing,
      getMultiEditingText: getText,
      // When editing, remove any typed value from the spreadsheet store state to prevent it from
      // overwriting an incoming edit on finalize. This handles the case where a cell was selected and
      // a single character was typed, triggering cell edit mode.
      getMultiEditingTypedValue: param?.editing && (param?.text ?? '') !== '' ? () => null : getTypedValue,
      focusBearerNode: focusBearer(),
    });
    if (param?.editing) {
      viewport.setTableCellEditorPosition(index);
    }
  };

  const setMultiIndices: TableInteractionClassBridge['setMultiIndices'] = (
    {start, end, text},
    options = {}
  ) => {
    if (!isMounted()) {
      return;
    }
    setState(computeMultiIndicesState({start, end}, options.multiSelIndex ?? null));
    const editingText = text != null ? text : getText(start);
    const typedValue = getTypedValue(start);
    setMultiEditing({editing: false, index: start, text: editingText, typedValue, selection: null});
    MultiSelectListUtil._focus(focusBearer());
  };

  const expandToCell = (index: TableIndex): Cell => getTableModel().mutations.expandToCell(index);

  /** The top left cell of the merged region containing `index`, or `index` when it is not merged. */
  const findUnmergedCell = (index: TableIndex): TableIndex => {
    const {table, computedTable} = getTableProps();
    if (!computeIsRealIndex(index, table)) {
      return index;
    }
    const mergedBox = computedTable[index[0]][index[1]]?.mergedBox;
    return mergedBox != null ? [mergedBox.minI, mergedBox.minJ] : index;
  };

  /** Commits `text` and `cellData` to the cell at `index`, leaving that cell selected. */
  const commitCellAndStay = (index: TableIndex, text: string, cellData: Partial<Cell>): void => {
    const {mutations} = getTableModel();
    mutations.saveSelection();
    mutations.updateCell(index, {text, ...cellData});

    const {selection, state} = getTableModel();
    const collapse =
      !selection.hasRealSelection ||
      compareTableIndex(state.multiStartIndex as TableIndex, state.multiEndIndex as TableIndex) === 0;
    const newIndex = findUnmergedCell(index);
    multiSelectSingle(newIndex, {
      editing: false,
      collapse,
      text: compareTableIndex(newIndex, index) === 0 ? text : null,
      multiActualIndex: index,
    });
    viewport.scrollCellIntoViewAfterEdit();
  };

  const canAttachFileToSelectedCell = (): boolean => {
    const index = getTableModel().state.multiSelIndex;
    return Boolean(index && getTableProps().canAttachFileToCell?.(index));
  };

  const attachFileToSelectedCell = async (file: File): Promise<void> => {
    if (!canAttachFileToSelectedCell()) {
      return;
    }
    // Capture the index before uploading, in case the selection changes meanwhile.
    const index = getTableModel().state.multiSelIndex!;
    const {convertFileToCellData} = getTableProps();
    assert(convertFileToCellData != null, 'Expected a table that accepts files to convert them to cell data');
    const {text, cellData} = await convertFileToCellData(file);
    if (isMounted()) {
      commitCellAndStay(index, text, cellData);
    }
  };

  const finalizeAndExitEditMode = (): void => {
    if (!isMounted()) {
      return;
    }
    const {multiSelIndex} = getTableModel().state;
    if (!multiSelIndex) {
      return;
    }
    finalizeEdit();
    multiSelectSingle(multiSelIndex, {
      collapse: false, // preserve selection
      editing: false, // but exit edit mode
    });
    setCellEditorPosition(null);
  };

  /** The row range spanning the table's effective column slice, from row `i1` to `i2`. */
  const rowRange = (i1: number, i2: number): {start: TableIndex; end: TableIndex} => {
    const {start: sliceStart, end: sliceEnd} = getTableModel().singleInputs.effectiveSlices;
    return {start: [Math.min(i1, i2), sliceStart], end: [Math.max(i1, i2), sliceEnd]};
  };

  /** The column range spanning every row, from column `j1` to `j2`. */
  const colRange = (j1: number, j2: number): {start: TableIndex; end: TableIndex} => {
    const lastRowIndex = getTableModel().dimensions.rowCount - 1;
    return {start: [0, Math.min(j1, j2)], end: [lastRowIndex, Math.max(j1, j2)]};
  };

  const fillDownMouseUpHandler = (): void => {
    const {fillDown, state, mutations} = getTableModel();
    const reset = {fillDownActive: false, fillDownBox: null, fillDownInvalid: false};
    if (!fillDown.isActive || fillDown.isInvalid) {
      setMultiExtra(reset, state.multiExtra);
      return;
    }
    try {
      // If this for any reason errors, unset fillDownActive to allow normal selections.
      mutations.performFillDown();
      MultiSelectListUtil._focus(focusBearer());
    } finally {
      setMultiExtra(reset, state.multiExtra);
    }
  };

  const handle: TableInteractionHandle = {
    axisCellDragStart: (options, e) => {
      if (!isMounted()) {
        return;
      }
      const {singleInputs, state, dimensions} = getTableModel();
      let {start} = options;
      const {active} = options;
      const {start: sliceStart, end: sliceEnd} = singleInputs.slices;
      const isRowSelection = active === TABLE_SELECTION_ROW;
      // for row selection, if the table is sliced then start the row selection at the sliceStart
      if (isRowSelection && sliceStart) {
        start = [start[0], sliceStart ?? 0];
      }
      let end = computeExpandedSelectionIndex(start, active, dimensions.rowCount, dimensions.columnCount);
      // for row selection, if the table is sliced then end the row selection at the sliceEnd
      if (isRowSelection && sliceEnd) {
        end = [end[0], sliceEnd ?? end[1]];
      }

      // If we are extending the selection, take the existing selected index,
      // and project it to the start of the desired selection dimension.
      if (e.shiftKey && state.multiSelIndex != null) {
        start = isRowSelection ? [state.multiSelIndex[0], sliceStart ?? 0] : [0, state.multiSelIndex[1]];
      }

      selectionMouseDown({start, end, active}, e);
    },

    axisCellDragOver: (index) => {
      if (!isMounted()) {
        return;
      }
      const {state, dimensions} = getTableModel();
      if (!state.draggingDimension) {
        selectionMouseOver(
          computeExpandedSelectionIndex(index, state.multiActive, dimensions.rowCount, dimensions.columnCount)
        );
      }
    },

    axisCellDragHandleMouseDown: (draggingDimension, index) => {
      if (!isMounted()) {
        return false;
      }
      const {state, selection, singleInputs, dimensions} = getTableModel();
      if (state.draggingDimension || getTableProps().locked) {
        checkSoftInvariant(!state.draggingDimension, {
          message: 'Expected not to be dragging already',
          expirationDate: '2026-02-01',
          errorInDevAndTest: true,
        });
        return false;
      }
      MultiSelectListUtil.cancelComposition(focusBearer());

      let selectionStart = state.multiStartIndex;
      let selectionEnd = state.multiEndIndex;

      let isIndexSelected = false; // Whether any cells in the dragged row/column are selected
      if (selection.hasRealSelection) {
        const {minI, maxI, minJ, maxJ} = selectionBox();
        // Extend selection to include whole row/column
        if (draggingDimension === TableDimension.ROW) {
          ({start: selectionStart, end: selectionEnd} = rowRange(minI, maxI));
          // Whether the row is in the selection AND the selection isn't every row
          isIndexSelected =
            index >= minI && index <= maxI && !(minI === 0 && maxI === dimensions.rowCount - 1);
        } else {
          // The minJ for column reordering should begin only with columns that are not Single Input.
          const firstNonSingleInputIndex = singleInputs.boundaryIndex;
          const newMinJ = Math.max(minJ, firstNonSingleInputIndex ?? minJ);
          ({start: selectionStart, end: selectionEnd} = colRange(newMinJ, maxJ));
          const {start: sliceStart, end: sliceEnd} = singleInputs.effectiveSlices;
          // Whether the column is in the selection AND the selection isn't every column
          isIndexSelected = index >= minJ && index <= maxJ && !(minJ <= sliceStart && maxJ >= sliceEnd);
        }
      }

      if (!isIndexSelected) {
        // Reset the selection to only the row/column that is being dragged
        ({start: selectionStart, end: selectionEnd} =
          draggingDimension === TableDimension.ROW ? rowRange(index, index) : colRange(index, index));
      }

      // Ensure that the selection includes any merged cells
      const {table, computedTable} = getTableProps();
      const expandedBox = computeExpandedSelectionBox(selectionStart, selectionEnd, table, computedTable);
      assert(expandedBox != null, 'Expected to have a selection');
      const {minI, maxI, minJ, maxJ} = expandedBox;
      setMultiIndices({
        start: [minI, minJ],
        end: [maxI, maxJ],
      });

      setState({draggingDimension});
      return true;
    },

    finalizeAndExitEditMode,

    freezeThroughColumn: (j) => {
      if (!isMounted()) {
        return;
      }
      const result = getTableModel().mutations.freezeThroughColumn(j, {
        canFreeze: ({widths}, siBoundary) => {
          const container = elements.horizontalScrollContainer;
          if (container && widths && widths.length > 0) {
            if (wouldFreezeExceedViewport(widths, siBoundary, j, container.clientWidth)) {
              ToastActions.pushToast({
                message: 'Cannot freeze: not enough room to display the next column.',
                type: ToastType.WARNING,
              });
              return false;
            }
          }
          return true;
        },
        beforePersist: finalizeAndExitEditMode,
      });
      if (result === 'merge-conflict') {
        showFreezeMergeConflictToast('freeze');
      }
    },

    unfreezeFromColumn: (j) => {
      if (!isMounted()) {
        return;
      }
      const result = getTableModel().mutations.unfreezeFromColumn(j, {
        beforePersist: finalizeAndExitEditMode,
      });
      if (result === 'merge-conflict') {
        showFreezeMergeConflictToast('unfreeze');
      }
    },

    cellDragStart: selectionMouseDown,

    cellDragOver: (index) => {
      if (!isMounted()) {
        return;
      }
      const {fillDown, state, dimensions} = getTableModel();
      if (!fillDown.isActive && !state.draggingDimension) {
        selectionMouseOver(
          computeExpandedSelectionIndex(index, state.multiActive, dimensions.rowCount, dimensions.columnCount)
        );
      }
    },

    cellMouseMoveHandler: (index) => {
      if (!isMounted()) {
        return;
      }
      const {fillDown, state} = getTableModel();
      if (!fillDown.isActive) {
        return;
      }
      const {clientX, clientY} = pointerPosition;
      const fillDownBox = computeFillDownDragBox(index, selectionBox(), cellRect(index[0], index[1])!, {
        clientX: clientX ?? NaN,
        clientY: clientY ?? NaN,
      });

      // Each write below is based on the multiExtra at the start of this move, so a later write
      // replaces any earlier one rather than building on it.
      if (computeContainsMergedRegion(getTableProps().computedTable, fillDownBox)) {
        setMultiExtra({fillDownInvalid: true}, state.multiExtra);
        return;
      }
      setMultiExtra({fillDownInvalid: false}, state.multiExtra);

      if (!isEqual(fillDown.box, fillDownBox)) {
        setMultiExtra({fillDownBox}, state.multiExtra);
      }
    },

    fillDownSquareMouseDownHandler: (e) => {
      if (!isMounted()) {
        return;
      }
      e.preventDefault(); // prevent browser drag behavior from changing selection
      MultiSelectListUtil.cancelComposition(focusBearer());

      const {selection, state} = getTableModel();
      assert(selection.hasMultiSelection, 'Expected to have a selection');
      // Because the fill down is dependent on the multiSelectionBox, we store fillDown state alongside
      // multiSelectionBox state, so that if it is cleared, it also clears the fillDown state.
      setMultiExtra({fillDownActive: true, fillDownBox: selectionBox()}, state.multiExtra);
      $(window).one('mouseup', fillDownMouseUpHandler);
    },

    fillDownSquareDoubleClickHandler: (e) => {
      if (!isMounted()) {
        return;
      }
      e.preventDefault();
      getTableModel().mutations.performDoubleClickFillDown();
      MultiSelectListUtil._focus(focusBearer());
    },

    selectCell: (index, options) => {
      if (!isMounted()) {
        return;
      }
      finalizeEdit();
      multiSelectSingle(index, options);
    },

    expandToAndEditCell: (index, selectAll = false) => {
      if (!isMounted()) {
        return;
      }
      const {locking} = getTableModel();
      if (locking.lockedWarningMessage) {
        notifications.warn(locking.lockedWarningMessage, {id: 'TableEditable.isLockedWithEditWarn'});
      }
      if (locking.isCellSelectionLocked) {
        return;
      }

      const [i, j] = index;
      let text: string | null;
      let typedValue: Optional<TypedValue>;
      let end: number;
      const {table, cid} = getTableProps();
      if (computeIsRealIndex(index, table)) {
        text = null;
        typedValue = null;
        end = table.table[i][j].text.length;
      } else {
        // A ghost cell: grow the table to reach it, which leaves an empty cell (keeping the precision
        // of the cell above when the table grows by rows).
        ({text, typedValue} = expandToCell(index));
        end = text.length;
      }

      multiSelectSingle(index, {
        text,
        typedValue: typedValue || null,
        editing: true,
        collapse: false,
        selection: {cid: computeCellCid(cid, i, j), start: selectAll ? 0 : end, end},
      });
    },

    multiPasteHandler: (e, options) => {
      if (!isMounted()) {
        return;
      }
      const {locked, table} = getTableProps();
      assert(!locked, 'Table paste handler unexpectedly called while locked');

      if (canAttachFileToSelectedCell()) {
        const filePromise = wyciwyp.extractImageFromPaste(e);
        if (filePromise) {
          filePromise.then((file) => {
            if (file) {
              attachFileToSelectedCell(file as File);
            }
          });
          return;
        }
      }

      e.preventDefault();

      // Use the clipboard's HTML when it holds a single table; otherwise build the table from the
      // plain text's newline and tab delimiters.
      const parts = wyciwyp.processClipboard(e.clipboardData, {
        allowTables: true,
        ignoreTableMinWidth: true,
        ignoreTableMinHeight: true,
        tableType: table._tableType,
      });
      const cells: Array<Array<PastedCell>> =
        parts.length === 1 && parts[0].type === 'table'
          ? (parts[0] as TableWYCIWYPPart).text.table
          : parseRawDataToCells(e.clipboardData.getData('text/plain'), table._tableType);

      const {mutations} = getTableModel();
      mutations.saveSelection();
      mutations.pasteTable(
        cells,
        {
          transpose: options?.transpose,
          selectionIndex: options?.selectionIndex,
          preserveDestinationFormatting: options?.preserveDestinationFormatting,
        },
        (args) => {
          setMultiIndices(args);
        }
      );
    },
  };

  const attach: TableElementAttachments = {
    focusBearer: (element) => {
      elements.focusBearer = element;
      if (element == null) {
        viewport.cancelPendingWork();
        $(window).off('mouseup', fillDownMouseUpHandler);
      }
    },
    horizontalScrollContainer: (element) => {
      elements.horizontalScrollContainer = element;
    },
    stickyHeader: (element) => {
      elements.stickyHeader = element;
    },
    virtualizationHandle: (handleInstance) => {
      elements.virtualizationHandle = handleInstance;
    },
    tableRoot: (element) => {
      elements.tableRoot = element;
    },
    pageVerticalScrollContainer: (container) => {
      elements.pageVerticalScrollContainer = container;
    },
  };

  return {
    ...handle,
    finalizeEdit,
    trackPointer: (clientX, clientY) => {
      pointerPosition.clientX = clientX;
      pointerPosition.clientY = clientY;
    },
    attach,
    classBridge: {viewport, multiSelectSingle, setMultiIndices, expandToCell},
  };
}

/**
 * The interactions of a table rendered without a `TableEditable` (an isolated store in a test, or a
 * standalone axis cell). Nothing ever attaches a focus bearer to them, so every interaction does
 * nothing.
 */
export const UNAVAILABLE_TABLE_INTERACTIONS: TableInteractions = createTableInteractions({
  getTableModel: () => {
    assert(false, 'This table has no interactions');
  },
  getTableProps: () => {
    assert(false, 'This table has no interactions');
  },
  setState: () => {
    assert(false, 'This table has no interactions');
  },
});
